<template>
  <div class="chart-container">
    <apexchart :height="`${height}px`" type="line" :options="chartOptions" :series="series"></apexchart>
  </div>
</template>

<style scoped>
  .chart-container :deep(.apexcharts-legend-marker) {
    border: 1px solid var(--kate-type-dark) !important;
  }

  .chart-container :deep(.apexcharts-tooltip-marker) {
    border: 1px solid var(--kate-type-dark) !important;
  }
</style>

<script>
export default {
  props: {
    yaxisTitle: {
      type: String,
    },
    xaxisTitle: {
      type: String,
    },
    yaxisMax: {
      type: Number,
    },
    yaxisFormatter: {
      type: Function,
    },
    xaxisData: {
      type: Array,
    },
    xaxisFormatter: {
      type: Function,
    },
    tooltipFormatter: {
      type: Function,
    },
    seriesBar: {
      type: Array,
    },
    seriesBarTitle: {
      type: [String, Array],
      default: 'Series One',
    },
    seriesLine: {
      type: Array,
    },
    seriesLineTitle: {
      type: String,
      default: 'Series Two',
    },
    stepSize: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 300,
    },
    customBarColors: {
      type: [String, Array],
      default: () => ['var(--kate-danger)', 'var(--kate-primary)', 'var(--kate-success)'],
    },
    customLineColor: {
      type: String,
      default: 'var(--kate-chart-combi-line-color)',
    },
    customTooltip: {
      type: Function,
    },
  },

  computed: {
    isStackedBar() {
      return Array.isArray(this.seriesBar) && this.seriesBar.length > 0 && Array.isArray(this.seriesBar[0]);
    },

    chartOptions() {
      return {
        chart: {
          type: 'bar',
          stacked: this.isStackedBar,
          stackType: 'normal',
          toolbar: {
            show: true,
            tools: {
              zoom: '<i class="far fa-object-group" style="cursor: default; color: var(--kate-type-alt-dark);" aria-hidden="true"></i>',
              zoomin: '<i class="fas fa-search-plus" aria-hidden="true"></i>',
              zoomout: '<i class="fas fa-search-minus" aria-hidden="true"></i>',
              pan: false,
              download: false,
              reset: false,
              selection: false,
            },
          },
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 1,
            opacity: 0.5,
            color: 'var(--kate-type-dark)',
          },
        },
        noData: {
          text: 'No data available',
          align: 'center',
          verticalAlign: 'middle',
          style: {
            fontSize: '20px',
            color: 'rgba(255, 255, 255, .5)',
          },
        },
        legend: {
          show: true,
          labels: {
            colors: 'var(--kate-type-primary)',
            useSeriesColors: false,
          },
          itemMargin: {
            horizontal: 5,
            vertical: 15,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
          markers: {
            size: 3,
            fillColors: this.customBarColors,
            radius: 2,
          },
        },
        grid: {
          show: true,
          borderColor: 'var(--kate-background-alpha)',
        },
        responsive: [{
          breakpoint: 1000,
          options: {},
        }],
        stroke: {
          width: 3,
          curve: 'straight',
          colors: this.customLineColor,
        },
        fill: {
          type: 'solid',
          colors: this.customBarColors,
        },
        markers: {
          size: 4,
          strokeColors: this.customLineColor,
          strokeWidth: 1,
          colors: this.customLineColor,
          strokeOpacity: 0.9,
          shape: 'circle',
          radius: 2,
          hover: {
            sizeOffset: 2,
          },
        },
        xaxis: {
          type: 'string',
          categories: this.xaxisData,
          tickAmount: 12,
          title: {
            text: this.xaxisTitle,
            style: {
              color: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
          },
          axisBorder: {
            color: 'var(--kate-background-alpha)',
            height: 1,
            width: '100%',
            offsetX: 0,
            offsetY: 0,
          },
          labels: {
            maxHeight: 120,
            style: {
              colors: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
            formatter: this.xaxisFormatter || undefined,
          },
        },
        yaxis: {
          min: 0,
          stepSize: this.stepSize,
          max: this.yaxisMax,
          showForNullSeries: false,
          tickAmount: this.yaxisMax <= 5 ? this.yaxisMax : 5,
          title: {
            text: this.yaxisTitle,
            style: {
              color: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
          },
          labels: {
            show: true,
            style: {
              colors: 'var(--kate-type-alt-dark)',
            },
            formatter: this.yaxisFormatter || (value => value.toFixed(2)),
          },
        },
        tooltip: {
          enabled: true,
          enabledOnSeries: false,
          fillSeriesColor: false,
          shared: true,
          theme: 'dark',
          style: {
            fontSize: '12px',
          },
          marker: {
            show: true,
            fillColors: this.customBarColors,
          },
          y: {
            formatter: this.tooltipFormatter || undefined,
          },
          custom: this.customTooltip || undefined,
        },
      };
    },

    series() {
      const series = [];
      if (this.isStackedBar) {
        this.seriesBar.forEach((seriesData, index) => {
          series.push({
            name: this.seriesBarTitle[index] || `Series ${index + 1}`,
            type: 'bar',
            data: seriesData || [],
          });
        });
      } else {
        series.push({
          name: this.seriesBarTitle,
          type: 'bar',
          data: this.seriesBar,
        });
      }
      if (this.seriesLine) {
        series.push({
          name: this.seriesLineTitle,
          type: 'line',
          data: this.seriesLine,
          color: this.customLineColor,
        });
      }
      return series;
    },
  },
};
</script>
